import {announce} from '@github-ui/aria-live'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useMemo, useState} from 'react'
import {sortFindingsBySeverity} from './entity-helpers'
import type {Finding, FindingResolvedReason} from './types'

const ignoreFindingIds = async (
  basePath: string,
  ids: string[],
  resolvedReason: FindingResolvedReason,
): Promise<Response> => {
  const response = await verifiedFetchJSON(`${basePath}/code_review_findings/ignore`, {
    method: 'POST',
    body: {entity_ids: ids, resolved_reason: resolvedReason},
  })
  if (!response.ok) {
    throw new Error(`Failed to ignore findings (status ${response.status} ${response.statusText})`)
  }
  return response
}

const unignoreFindingIds = async (basePath: string, ids: string[]): Promise<Response> => {
  const response = await verifiedFetchJSON(`${basePath}/code_review_findings/ignore`, {
    method: 'DELETE',
    body: {entity_ids: ids},
  })
  if (!response.ok) {
    throw new Error(`Failed to unignore findings (status ${response.status} ${response.statusText})`)
  }
  return response
}

type LocalOverride = {isIgnored: true; resolvedReason: FindingResolvedReason} | {isIgnored: false}

export function useIgnoreFindings(findings: Finding[], pullRequestBasePath: string) {
  // Track only local optimistic overrides.
  // The server-side ignored state is derived from each finding's resolvedAt.
  const [localOverrides, setLocalOverrides] = useState<Map<string, LocalOverride>>(() => new Map())
  const [actionError, setActionError] = useState<string | null>(null)

  const handleDismiss = useCallback(
    async (findingsToDismiss: Finding[], resolvedReason: FindingResolvedReason = 'wont_fix') => {
      const ids = findingsToDismiss.map(f => f.id)
      setActionError(null)
      setLocalOverrides(prev => {
        const next = new Map(prev)
        for (const id of ids) next.set(id, {isIgnored: true, resolvedReason})
        return next
      })
      try {
        await ignoreFindingIds(pullRequestBasePath, ids, resolvedReason)
        const count = ids.length
        announce(`${count} ${count === 1 ? 'finding' : 'findings'} resolved`)
      } catch {
        // Rollback optimistic state on failure
        setLocalOverrides(prev => {
          const next = new Map(prev)
          for (const id of ids) next.delete(id)
          return next
        })
        const message = `Failed to resolve ${ids.length === 1 ? 'finding' : 'findings'}. Please try again.`
        setActionError(message)
      }
    },
    [pullRequestBasePath],
  )

  const handleReopen = useCallback(
    async (findingsToReopen: Finding[]) => {
      const ids = findingsToReopen.map(f => f.id)
      setActionError(null)
      setLocalOverrides(prev => {
        const next = new Map(prev)
        for (const id of ids) next.set(id, {isIgnored: false})
        return next
      })
      try {
        await unignoreFindingIds(pullRequestBasePath, ids)
        const count = ids.length
        announce(`${count} ${count === 1 ? 'finding' : 'findings'} reopened`)
      } catch {
        // Rollback optimistic state on failure
        setLocalOverrides(prev => {
          const next = new Map(prev)
          for (const id of ids) next.delete(id)
          return next
        })
        const message = `Failed to reopen ${ids.length === 1 ? 'finding' : 'findings'}. Please try again.`
        setActionError(message)
      }
    },
    [pullRequestBasePath],
  )

  const ignoredIds = useMemo(() => {
    const ids = new Set(findings.filter(f => f.resolvedAt).map(f => f.id))
    for (const [id, override] of localOverrides) {
      if (override.isIgnored) ids.add(id)
      else ids.delete(id)
    }
    return ids
  }, [findings, localOverrides])

  const openFindings = useMemo(
    () => sortFindingsBySeverity(findings.filter(f => !ignoredIds.has(f.id))),
    [findings, ignoredIds],
  )

  const ignoredFindings = useMemo(() => {
    return sortFindingsBySeverity(
      findings
        .filter(f => ignoredIds.has(f.id))
        .map(f => {
          const override = localOverrides.get(f.id)
          return override?.isIgnored ? {...f, resolvedReason: override.resolvedReason} : f
        }),
    )
  }, [findings, ignoredIds, localOverrides])

  return {openFindings, ignoredFindings, handleDismiss, handleReopen, actionError}
}
